import { auth } from '../../utils/database/firebase-config';
import { signOut } from 'firebase/auth';

const STORAGE_KEY = 'lastActivityAt';

class SessionManager {
  constructor() {
    this.checkIntervalId = null;
    this.sessionDuration = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    this.warningTime = 10 * 60 * 1000; // 10 minutes before logout
    this.checkInterval = 30 * 1000; // re-check every 30 seconds
    this.activityThrottle = 5 * 1000; // throttle activity writes to once per 5s
    this.lastActivity = Date.now();
    this.lastActivityWrite = 0;
    this.isWarningShown = false;
    this.isMonitoring = false;

    // Bind methods
    this.handleUserActivity = this.handleUserActivity.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
    this.tick = this.tick.bind(this);
    this.logout = this.logout.bind(this);
    this.showWarning = this.showWarning.bind(this);
  }

  startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    // Seed lastActivity from storage so reloads / multi-tab keep continuity.
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    this.lastActivity = stored && Number.isFinite(stored) ? stored : Date.now();
    if (!stored) {
      this.persistActivity(this.lastActivity);
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, true);
    });
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('focus', this.handleVisibility);
    window.addEventListener('storage', this.handleStorage);

    // Periodic check is resilient to setTimeout throttling in background tabs:
    // each tick we compare wall-clock now() vs lastActivity, so even if ticks
    // were skipped/delayed we'll catch up the moment the tab is foregrounded.
    this.checkIntervalId = setInterval(this.tick, this.checkInterval);

    // Run an immediate check.
    this.tick();
  }

  persistActivity(ts) {
    try {
      localStorage.setItem(STORAGE_KEY, String(ts));
    } catch {
      /* storage may be unavailable */
    }
  }

  handleUserActivity() {
    const now = Date.now();
    this.lastActivity = now;

    // Throttle writes to localStorage so we don't hammer it on every mousemove.
    if (now - this.lastActivityWrite > this.activityThrottle) {
      this.lastActivityWrite = now;
      this.persistActivity(now);
    }

    if (this.isWarningShown) {
      this.hideWarning();
    }
  }

  handleVisibility() {
    // When the tab becomes visible again, immediately re-check elapsed time.
    if (!document.hidden) {
      this.tick();
    }
  }

  handleStorage(e) {
    // Activity in another tab counts — pull their timestamp in.
    if (e.key === STORAGE_KEY && e.newValue) {
      const ts = Number(e.newValue);
      if (Number.isFinite(ts) && ts > this.lastActivity) {
        this.lastActivity = ts;
        if (this.isWarningShown) this.hideWarning();
      }
    }
  }

  tick() {
    const elapsed = Date.now() - this.lastActivity;
    if (elapsed >= this.sessionDuration) {
      this.logout();
      return;
    }
    if (elapsed >= this.sessionDuration - this.warningTime) {
      this.showWarning();
    }
  }

  showWarning() {
    if (this.isWarningShown) return;
    
    this.isWarningShown = true;
    
    // Create warning modal
    const modal = document.createElement('div');
    modal.id = 'session-warning-modal';
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md mx-4 text-center">
        <div class="text-yellow-500 text-4xl mb-4">⚠️</div>
        <h3 class="text-lg font-semibold mb-2">تحذير انتهاء الجلسة</h3>
        <p class="text-gray-600 mb-4">ستنتهي جلستك خلال 10 دقائق بسبب عدم النشاط. انقر على "متابعة" للبقاء متصلاً.</p>
        <div class="flex gap-3 justify-center">
          <button id="continue-session" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            متابعة الجلسة
          </button>
          <button id="logout-now" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
            تسجيل الخروج
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Add event listeners
    document.getElementById('continue-session').addEventListener('click', () => {
      this.hideWarning();
      // Force-refresh activity (bypass the throttle).
      this.lastActivity = Date.now();
      this.lastActivityWrite = this.lastActivity;
      this.persistActivity(this.lastActivity);
    });
    
    document.getElementById('logout-now').addEventListener('click', () => {
      this.logout();
    });
  }

  hideWarning() {
    const modal = document.getElementById('session-warning-modal');
    if (modal) {
      modal.remove();
    }
    this.isWarningShown = false;
  }

  async logout() {
    try {
      if (this.checkIntervalId) {
        clearInterval(this.checkIntervalId);
        this.checkIntervalId = null;
      }
      this.hideWarning();
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }

      await signOut(auth);

      localStorage.clear();
      sessionStorage.clear();

      alert('تم تسجيل خروجك تلقائياً بسبب عدم النشاط لمدة ساعتين.');
      window.location.reload();
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.reload();
    }
  }

  destroy() {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity, true);
    });
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('focus', this.handleVisibility);
    window.removeEventListener('storage', this.handleStorage);

    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    this.hideWarning();
    this.isMonitoring = false;
  }

  // Get remaining session time
  getRemainingTime() {
    const elapsed = Date.now() - this.lastActivity;
    const remaining = this.sessionDuration - elapsed;
    return Math.max(0, remaining);
  }

  // Check if session is about to expire
  isSessionExpiring() {
    return this.getRemainingTime() <= this.warningTime;
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
export default SessionManager;